import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import browser from '@openclassrooms/utils/browser';

interface Props {
  children: JSX.Element;
}

function waitForElementToExist(selector: string) {
  return new Promise((resolve) => {
    try {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
      }

      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          resolve(document.querySelector(selector));
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        subtree: true,
        childList: true,
      });

      setTimeout(() => {
        observer.disconnect();
      }, 10000);
    } catch (e) {
      // Ignore error (hash is not a valid DOM selector)
    }
  });
}

function useScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      waitForElementToExist(location.hash).then((element: HTMLElement) => {
        element.scrollIntoView({
          behavior: 'smooth',
          inline: 'nearest',
        });
      });
    }
  }, [location]);
}

export default function ScrollToTop({ children }: Props) {
  const location = useLocation();
  useScrollToHash();

  useEffect(() => {
    if (location.state?.preventScrollReset) {
      return;
    }
    browser.window.scrollTo(0, 0);
  }, [location]);

  return children;
}
